import React from 'react';
import LegalLayout from './LegalLayout';

export default function TermsOfService() {
  return (
    <LegalLayout
      title="FABTEXS AI Terms of Service"
      description="Terms of Service governing use of FABTEXS AI — the WhatsApp commerce automation platform for textile wholesalers, operated by FABTEXS – India’s Wholesale Fabric Hub."
      lastUpdated="18 February 2026"
    >
      <p>
        These Terms of Service (the &quot;<strong>Terms</strong>&quot;) form a legally binding agreement between
        you (&quot;<strong>seller</strong>&quot;, &quot;<strong>vendor</strong>&quot;, &quot;<strong>you</strong>&quot;) and{' '}
        <strong>FABTEXS – India’s Wholesale Fabric Hub</strong>, the operator of the brand &quot;<strong>FABTEXS AI</strong>&quot; and the
        platform available at <a href="https://app.fabtexs.com">app.fabtexs.com</a>
        {' '}and <a href="https://www.fabtexs.com">www.fabtexs.com</a> (collectively, the
        &quot;<strong>Platform</strong>&quot;). By creating an account, connecting a WhatsApp Business number,
        or otherwise using the Platform, you agree to these Terms.
      </p>

      <h2>1. The service</h2>
      <p>
        FABTEXS AI is a software-as-a-service (SaaS) tool that helps textile wholesalers
        manage product catalogs, broadcast offers to customers via WhatsApp, automate AI
        replies, manage orders, track payments, and handle related commerce workflows.
        FABTEXS does <strong>not</strong> own or operate WhatsApp; it integrates with the official
        Meta WhatsApp Business Cloud API using credentials you provide or authorize via
        Facebook Login for Business.
      </p>

      <h2>2. Eligibility &amp; account</h2>
      <ul>
        <li>You must be at least 18 years old and authorized to bind your business to these Terms.</li>
        <li>You must provide accurate registration information and keep it up to date.</li>
        <li>You are responsible for safeguarding your account credentials and for all activity that occurs under your account.</li>
        <li>One account per legal entity unless otherwise agreed in writing.</li>
      </ul>

      <h2>3. WhatsApp Business integration</h2>
      <p>
        When you connect a WhatsApp Business number to FABTEXS — either by pasting Meta
        Cloud API credentials or via Facebook Login for Business — you authorize FABTEXS
        Export to act on your behalf to send and receive messages on the connected number,
        subscribe webhooks, and read message status events.
      </p>
      <p>You acknowledge and agree that:</p>
      <ul>
        <li>You will comply with the <a href="https://www.whatsapp.com/legal/business-policy/">WhatsApp Business Messaging Policy</a> and the <a href="https://www.whatsapp.com/legal/commerce-policy/">WhatsApp Commerce Policy</a>.</li>
        <li>You will obtain valid opt-in consent from every buyer you message through FABTEXS, and honour opt-outs promptly.</li>
        <li>You will not use FABTEXS to send unsolicited (spam) messages, fraudulent messages, or messages that violate Indian law (including the IT Act 2000, Consumer Protection Act, and DPDP Act 2023).</li>
        <li>You are solely responsible for the content of messages sent through your connected number, including any AI-drafted replies you choose to send.</li>
        <li>WhatsApp may rate-limit, suspend, or ban your number for policy violations. FABTEXS has no control over Meta&apos;s enforcement actions and is not liable for them.</li>
      </ul>

      <h2>4. Customer data &amp; consent</h2>
      <p>
        FABTEXS lets you import customer contacts via CSV, Excel, paste text, and
        screenshot OCR, then broadcast or AI-reply to them. By importing a contact you
        warrant that:
      </p>
      <ul>
        <li>You have a lawful basis (typically consent) to message that buyer.</li>
        <li>The phone numbers were not scraped, purchased, or otherwise obtained without consent.</li>
        <li>You will treat opt-out requests as binding for FABTEXS broadcasts, AI replies, and any other outbound activity.</li>
      </ul>
      <p>
        You remain the data fiduciary / controller for your buyers&apos; data. FABTEXS acts as a
        processor under your instructions. See the <a href="/privacy-policy">Privacy Policy</a> for details.
      </p>

      <h2>5. AI assistant features</h2>
      <p>
        FABTEXS uses third-party large-language models (Claude Sonnet 4.5, Claude Haiku 4.5,
        Claude Vision) to draft replies, classify intent, generate broadcasts, and read
        screenshots. You understand that:
      </p>
      <ul>
        <li>AI output may be inaccurate, incomplete, or inappropriate. <strong>You are responsible</strong> for reviewing and approving AI suggestions before they are sent to buyers, except where you have explicitly enabled Autopilot for a specific number.</li>
        <li>AI output is not legal, tax, or commercial advice.</li>
        <li>Anthropic processes content you submit to AI features under its own data protection terms and does not train its production models on customer data.</li>
        <li>You will not use AI features to generate deceptive, defamatory, infringing, or illegal content.</li>
      </ul>

      <h2>6. Subscription, billing &amp; refunds</h2>
      <ul>
        <li>FABTEXS plans are billed in advance on a monthly or annual basis through Razorpay. Prices are listed at <a href="/pricing">/pricing</a> and may be revised with 30 days&apos; prior notice for existing subscribers.</li>
        <li>All fees are exclusive of GST and other applicable taxes, which will be added at the prevailing rate at the time of billing.</li>
        <li>Subscriptions auto-renew unless cancelled at least 24 hours before the renewal date.</li>
        <li>Refunds: prepaid annual fees are refundable on a pro-rata basis within the first 14 days if you have not used FABTEXS to send broadcasts beyond your free-trial allowance. Monthly fees are non-refundable once the billing cycle has begun. WhatsApp conversation charges levied directly by Meta are non-refundable.</li>
        <li>Failed payments suspend access after 7 days. You may resume by clearing dues at any time.</li>
      </ul>

      <h2>7. Acceptable use</h2>
      <p>You agree not to, and not to permit any third party to:</p>
      <ul>
        <li>Use the Platform to send spam, phishing, MLM / chain messages, adult content, or any content that violates applicable law.</li>
        <li>Reverse-engineer, decompile, or attempt to extract source code from the Platform.</li>
        <li>Bypass or attempt to bypass rate limits, plan limits, or authentication.</li>
        <li>Use FABTEXS to compete directly with FABTEXS (i.e., build a competing WhatsApp commerce SaaS).</li>
        <li>Upload malware, infringe IP rights, or attempt to gain unauthorized access to other sellers&apos; data.</li>
        <li>Use any automated agents, scripts, or scraping tools that put unreasonable load on the Platform.</li>
      </ul>
      <p>
        We may suspend or terminate accounts in clear violation of these Acceptable Use
        provisions without prior notice.
      </p>

      <h2>8. Intellectual property</h2>
      <ul>
        <li>The Platform, including its software, design, FABTEXS brand and AI prompts, is owned by FABTEXS – India’s Wholesale Fabric Hub and protected by Indian and international IP laws.</li>
        <li>You retain ownership of your catalog, customer data, and content. You grant FABTEXS a worldwide, non-exclusive, royalty-free licence to host, process, and transmit your content solely to operate the Platform for you.</li>
        <li>Feedback you give us about the Platform may be used by us without obligation or compensation.</li>
      </ul>

      <h2>9. Availability &amp; service levels</h2>
      <p>
        We target 99.5% monthly uptime for the FABTEXS application, excluding scheduled
        maintenance, third-party outages (Meta, Anthropic, Razorpay, hosting providers),
        and force-majeure events. The Platform is provided on an &quot;as-is&quot; and
        &quot;as-available&quot; basis. We do not guarantee uninterrupted, error-free, or
        secure-against-all-threats operation.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law:
      </p>
      <ul>
        <li>In no event will FABTEXS, its directors, employees, or sub-processors be liable for any indirect, incidental, consequential, special, or punitive damages — including lost profits, lost goodwill, or business interruption — arising out of or related to the Platform.</li>
        <li>Our aggregate liability for any claim arising out of or related to these Terms will not exceed the amount you paid FABTEXS for the Platform in the 6 months immediately preceding the event giving rise to the claim, or INR 10,000, whichever is greater.</li>
      </ul>

      <h2>11. Indemnity</h2>
      <p>
        You agree to defend, indemnify, and hold harmless FABTEXS – India’s Wholesale Fabric Hub and its
        affiliates from any claim, loss, or expense (including reasonable legal fees)
        arising out of: (a) your breach of these Terms; (b) content you transmit through
        the Platform; (c) your violation of WhatsApp / Meta policies; or (d) any third-
        party claim by a buyer that you messaged them without consent.
      </p>

      <h2>12. Termination</h2>
      <ul>
        <li>You may terminate your account at any time from Settings → Close Account.</li>
        <li>We may terminate or suspend your account immediately if you breach these Terms, use the Platform unlawfully, or if a payment becomes more than 30 days overdue.</li>
        <li>Upon termination, your data will be retained for up to 18 months for tax / audit purposes and then deleted, except where law requires longer retention.</li>
      </ul>

      <h2>13. Governing law &amp; dispute resolution</h2>
      <p>
        These Terms are governed by the laws of India. The courts of Mumbai, Maharashtra,
        will have exclusive jurisdiction over any disputes, subject to mandatory
        arbitration under the Arbitration &amp; Conciliation Act 1996 where the disputed
        amount exceeds INR 50,000. The seat of arbitration will be Mumbai, and the
        language will be English.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may modify these Terms by posting an updated version at this URL and emailing
        you at the address registered on your account. Material changes take effect 14
        days after notice. Continued use after the effective date constitutes acceptance.
      </p>

      <h2>15. Contact</h2>
      <p>
        Questions about these Terms? Email{' '}
        <a href="mailto:fabtexswholesale@gmail.com">fabtexswholesale@gmail.com</a> or
        write to FABTEXS – India’s Wholesale Fabric Hub — see the <a href="/contact-us">Contact Us</a> page.
      </p>
    </LegalLayout>
  );
}
